import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Button,
  Stack,
  Typography,
  Tooltip,
  Paper,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  WhatsApp,
  FormatListBulleted,
  Campaign,
} from "@mui/icons-material";
import { CircularLoading, NoDataWarning } from "@/components/common";
import { GenericTable } from "@/components/GenericTable/GenericTable";
import { useFormatter } from "@/utils/useFormatter";
import { PatientSearchActions } from "@/pages/patientsearch/components/PatientSearchActions";
import { FormTextInput } from "@/components/form";
import { useRegex } from "@/utils/useRegex";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { currentFeatures, oids } from "@/configs/Settings";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import dayjs from "dayjs";
import { SendWhatsAppMessageDialog } from "src-next/modules/crm-clinical/messaging";
import type {
  MessagingAppointment,
  MessagingPatient,
} from "src-next/modules/crm-clinical/messaging";
import { useMessagingTemplatesForSend } from "src-next/modules/crm-clinical/messaging";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { usePostAttendanceContext } from "@/contexts/PostAttendanceContext";

interface PatientSearchProps {
  patients: fhir5.BundleEntry<fhir5.Patient>[];
  loading: boolean;
}

export const PatientSearch: React.FC<PatientSearchProps> = ({
  patients,
  loading,
}) => {
  const { organizationId } = useOrganizationContext();
  const { contractPostAttendance } = usePostAttendanceContext();
  const { user } = useSessionContext();
  const navigate = useNavigate();
  const { control, setValue, watch } = useForm();
  const watchSearch = watch("search");
  const [showAllPatients, setShowAllPatients] = useState(false);

  const [isSendWhatsAppDialogOpen, setIsSendWhatsAppDialogOpen] =
    useState(false);
  const [selectedMessagingPatient, setSelectedMessagingPatient] =
    useState<MessagingPatient | null>(null);

  // Estado para armazenar dados dos tutores mapeados por ID do paciente
  // Formato: { [patientId]: { name: string, phone: string } }
  const [tutorsData, setTutorsData] = useState<
    Record<string, { name: string; phone: string; identifier: string }>
  >({});
  const [loadingTutors, setLoadingTutors] = useState(false);

  // Estados para controlar paginação e buscar tutores apenas da página atual
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const lastOrgIdRef = React.useRef<string | undefined>(undefined);

  useEffect(() => {
    /**
     * Segurança: ao trocar de organização, precisamos resetar estados locais
     * que podem manter a lista anterior visível (ex.: "Listar Todos", busca,
     * paginação e cache de tutores).
     */
    if (lastOrgIdRef.current === undefined) {
      lastOrgIdRef.current = organizationId;
      return;
    }

    if (lastOrgIdRef.current !== organizationId) {
      lastOrgIdRef.current = organizationId;
      setShowAllPatients(false);
      setValue("search", "");
      setCurrentPage(0);
      setItemsPerPage(5);
      setTutorsData({});
    }
  }, [organizationId, setValue]);

  // Função para alternar entre lista completa e busca
  const toggleShowAll = () => {
    setShowAllPatients(!showAllPatients);
    if (!showAllPatients) {
      setValue("search", ""); // Limpa a busca ao mostrar lista completa
    }
  };

  const columns = [
    { column: "Nome", rowName: "name" },
    {
      column: currentFeatures.defaultApp ? "CPF" : "ID",
      rowName: "identifier",
    },
    { column: "Data de nascimento", rowName: "birthDate" },
    { column: "Sexo de nascimento", rowName: "gender" },
    ...(currentFeatures.defaultApp
      ? [
          {
            column: "Telefone",
            rowName: "contact",
          },
        ]
      : []),
    // Adiciona colunas de tutor quando não for defaultApp (modo vet)
    ...(!currentFeatures.defaultApp
      ? [
          {
            column: "Nome do Tutor",
            rowName: "tutorName",
          },
          {
            column: "Telefone do Tutor",
            rowName: "tutorPhone",
          },
          {
            column: "CPF do Tutor",
            rowName: "tutorIdentifier",
          },
        ]
      : []),
  ];

  const { templates: messagingTemplates, isLoading: isTemplatesLoading } =
    useMessagingTemplatesForSend({ organizationId });

  const messagingAppointment: MessagingAppointment = useMemo(() => {
    return { id: "ad-hoc", startAtIso: dayjs().toISOString() };
  }, []);

  const handleOpenSendWhatsAppDialog = useCallback(
    (patient: MessagingPatient) => {
      setSelectedMessagingPatient(patient);
      setIsSendWhatsAppDialogOpen(true);
    },
    [],
  );

  // Mapeia os pacientes para linhas da tabela, incluindo dados dos tutores quando disponíveis
  const rows =
    patients?.map((el) => {
      const patientId = el.resource?.id || "";
      const rawCpfFromId =
        currentFeatures.defaultApp && patientId?.includes("-")
          ? patientId.split("-")[1] || ""
          : "";
      const identifier = currentFeatures.defaultApp
        ? useFormatter.formatCPF(rawCpfFromId)
        : el.resource?.identifier?.[0]?.value;

      const auxName =
        el.resource?.name?.find(
          (el: any) => el.use === "usual" || el.use === "official",
        )?.text || "-";
      const name = useFormatter.formatNameDisplay(auxName) || "-";
      const birthDate =
        el.resource?.birthDate &&
        `${useFormatter.formatDate(
          el.resource?.birthDate,
        )} - ${useFormatter.formatAge(el.resource?.birthDate)} anos`;
      const gender = useFormatter.formatGender(el.resource?.gender) || "-";

      const contact = el?.resource?.telecom;
      const phone = contact?.find((e: any) => e.system === "phone")?.value;

      // Busca dados do tutor se disponíveis (apenas para modo vet)
      const tutorData = tutorsData[patientId];
      const tutorName = tutorData?.name || "-";
      const tutorPhone = tutorData?.phone || "";
      const tutorIdentifier = tutorData?.identifier || "";

      return {
        identifier,
        rawCpfFromId,
        name,
        birthDate,
        patientId, // Armazena o ID do paciente para referência
        phoneRaw: phone || "",
        ...(phone && {
          contact: (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              gap={0.5}
            >
              <Typography variant="body2" fontSize="0.8125rem">
                {useFormatter.formatPhoneNumber(phone || "")}
              </Typography>
              <Tooltip title={useFormatter.formatPhoneNumber(phone || "")}>
                <IconButton
                  color="primary"
                  size="small"
                  // disabled={isTemplatesLoading || messagingTemplates.length === 0}
                  onClick={() =>
                    handleOpenSendWhatsAppDialog({
                      id: patientId || `patient-${name}`,
                      fullName: name,
                      cpf: rawCpfFromId || undefined,
                      phone: phone || undefined,
                    })
                  }
                >
                  <WhatsApp sx={{ fontSize: "1.15rem" }} />
                </IconButton>
              </Tooltip>
            </Stack>
          ),
        }),
        gender,
        // Adiciona dados do tutor quando não for defaultApp
        ...(!currentFeatures.defaultApp && {
          tutorName,
          tutorPhoneRaw: tutorPhone,
          tutorPhone: tutorPhone ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              gap={0.5}
            >
              <Typography variant="body2" fontSize="0.8125rem">
                {useFormatter.formatPhoneNumber(tutorPhone)}
              </Typography>
              <Tooltip title={useFormatter.formatPhoneNumber(tutorPhone)}>
                <IconButton
                  color="primary"
                  size="small"
                  disabled={
                    isTemplatesLoading || messagingTemplates.length === 0
                  }
                  onClick={() =>
                    handleOpenSendWhatsAppDialog({
                      id: `tutor-of-${patientId || name}`,
                      fullName: `Tutor de ${name}`,
                      cpf: tutorIdentifier || undefined,
                      phone: tutorPhone || undefined,
                      isTutor: true,
                    })
                  }
                >
                  <WhatsApp sx={{ fontSize: "1.15rem" }} />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            "-"
          ),
          tutorIdentifier,
        }),
      };
    }) || [];

  const filteredRows = watchSearch
    ? rows.filter((row) => {
        const searchLower = watchSearch.toLowerCase();
        return (
          row.name.toLowerCase().includes(searchLower) ||
          row.identifier
            ?.toLowerCase()
            ?.replace(/[^a-zA-Z0-9]/g, "")
            ?.includes(searchLower.replace(/[^a-zA-Z0-9]/g, "")) ||
          row.tutorIdentifier
            ?.toLowerCase()
            ?.replace(/[^a-zA-Z0-9]/g, "")
            ?.includes(searchLower.replace(/[^a-zA-Z0-9]/g, "")) ||
          row?.tutorName?.toLowerCase().includes(searchLower)
        );
      })
    : showAllPatients
      ? rows
      : [];

  // Busca tutores apenas para os pacientes visíveis na página atual
  useEffect(() => {
    // Só busca tutores se não for defaultApp e houver pacientes filtrados
    if (currentFeatures.defaultApp || !filteredRows.length || loading) {
      return;
    }

    // Calcula quais pacientes estão visíveis na página atual
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const visibleRows = filteredRows.slice(startIndex, endIndex);

    // Extrai IDs dos pacientes visíveis na página atual
    // Só busca se ainda não tiver os dados em cache
    const visiblePatientIds = visibleRows
      .map((row) => row.patientId)
      .filter((id) => id && !tutorsData[id]);

    if (visiblePatientIds.length === 0) {
      return;
    }

    // Busca tutores para os pacientes visíveis
    const fetchTutors = async () => {
      setLoadingTutors(true);
      try {
        // Concatena os IDs com vírgula para buscar todos de uma vez
        const patientIdsParam = visiblePatientIds.join(",");

        const response = await GetRelatedPerson({
          patient: patientIdsParam,
          vetRelated: true, // Indica que é para sistema vet
        });

        if (response?.data?.entry) {
          // Mapeia os tutores por ID do paciente
          const tutorsMap: Record<
            string,
            { name: string; phone: string; identifier: string }
          > = {};

          response.data.entry.forEach((entry: any) => {
            const relatedPerson = entry.resource;
            if (relatedPerson?.patient?.reference) {
              // Extrai o ID do paciente da referência (formato: "Patient/ID")
              const patientRef = relatedPerson.patient.reference;
              const patientId = patientRef.replace("Patient/", "");

              // Extrai nome do tutor
              const tutorNameObj = relatedPerson.name?.find(
                (n: any) => n.use === "official" || n.use === "usual",
              );
              const tutorName = tutorNameObj?.text || "-";

              // Extrai telefone do tutor
              const tutorPhoneObj = relatedPerson.telecom?.find(
                (t: any) => t.system === "phone",
              );
              const tutorPhone = tutorPhoneObj?.value || "";

              const tutorIdentifier =
                relatedPerson?.identifier?.[0]?.value || "";

              if (patientId) {
                tutorsMap[patientId] = {
                  name: useFormatter.formatNameDisplay(tutorName) || "-",
                  phone: tutorPhone,
                  identifier: tutorIdentifier,
                };
              }
            }
          });

          // Atualiza o estado com os tutores encontrados
          setTutorsData((prev) => ({
            ...prev,
            ...tutorsMap,
          }));
        }
      } catch (error) {
        console.error("Erro ao buscar tutores:", error);
      } finally {
        setLoadingTutors(false);
      }
    };

    fetchTutors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filteredRows.length, loading]);

  // Handlers para paginação (serão passados para o GenericTable através de uma versão customizada)
  const handleChangePage = (_: unknown, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  return (
    <>
      <Stack spacing={2}>
        {/* Barra de Pesquisa */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              borderRadius: 2,
            }}
          >
            <FormTextInput
              name="search"
              label={
                currentFeatures.defaultApp
                  ? "Pesquisar paciente"
                  : "Pesquisar pet ou tutor"
              }
              control={control}
              autoFocus={true}
            />
          </Box>

          <Stack direction="row" spacing={1}>
            {/* Botão para listar todos */}
            <Button
              variant="contained"
              onClick={toggleShowAll}
              startIcon={<FormatListBulleted />}
              sx={{
                color: "primary.main",
                bgcolor: "white",
                "&:hover": {
                  bgcolor: "white",
                  opacity: 0.8,
                },
                px: 3,
                py: 1,
                minWidth: "auto",
                whiteSpace: "nowrap",
              }}
            >
              {showAllPatients ? "Ocultar Lista" : "Listar Todos"}
            </Button>
            <Button
              variant="contained"
              onClick={() =>
                navigate(contractPostAttendance ? "/fluxzap" : "/extensions")
              }
              startIcon={<WhatsApp />}
              sx={{
                color: "primary.main",
                bgcolor: "white",
                "&:hover": {
                  bgcolor: "white",
                  opacity: 0.8,
                },
                px: 3,
                py: 1,
                minWidth: "auto",
                whiteSpace: "nowrap",
              }}
            >
              FluxZap
            </Button>
          </Stack>
        </Box>

        {/* Resultados da Pesquisa */}
        {(watchSearch || showAllPatients) && (
          <Paper
            elevation={0}
            sx={{
              background: "white",
              borderRadius: "10px",
              p: 2,
            }}
          >
            {loading ? (
              <CircularLoading />
            ) : filteredRows.length === 0 ? (
              <NoDataWarning message="Nenhum paciente encontrado com os critérios informados" />
            ) : (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {watchSearch ? (
                        <>
                          <SearchIcon fontSize="small" />
                          Resultados da Busca
                        </>
                      ) : showAllPatients ? (
                        <>
                          <FormatListBulleted fontSize="small" />
                          Lista Completa de{" "}
                          {currentFeatures.defaultApp ? "Pacientes" : "Pets "}
                        </>
                      ) : null}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      ({filteredRows.length} pacientes)
                    </Typography>
                  </Stack>
                  {watchSearch && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontStyle: "italic" }}
                    >
                      Filtrando por: "{watchSearch}"
                    </Typography>
                  )}
                </Stack>
                <GenericTable
                  tableColumns={columns}
                  tableRows={filteredRows}
                  actions={(rowData: any) => (
                    <PatientSearchActions row={rowData} />
                  )}
                  // Usa paginação controlada apenas quando não for defaultApp (modo vet)
                  // para poder buscar tutores apenas da página atual
                  {...(!currentFeatures.defaultApp && {
                    page: currentPage,
                    rowsPerPage: itemsPerPage,
                    onPageChange: handleChangePage,
                    onRowsPerPageChange: handleChangeRowsPerPage,
                  })}
                />
              </>
            )}
          </Paper>
        )}
      </Stack>

      {selectedMessagingPatient && (
        <SendWhatsAppMessageDialog
          open={isSendWhatsAppDialogOpen}
          onClose={() => {
            setIsSendWhatsAppDialogOpen(false);
            setSelectedMessagingPatient(null);
          }}
          organizationId={organizationId}
          practitionerId={`${oids.cpf}-${user?.username}`}
          appointment={messagingAppointment}
          patient={selectedMessagingPatient}
          templates={messagingTemplates}
        />
      )}
    </>
  );
};
